// «Spedito» e cambio stato di UN ordine, dalla pagina ordine (redesign
// 2026-10-23). Prima si potevano fare solo dalla lista, selezionando l'ordine:
// stessi endpoint e stesso dialog della lista, con un ref solo.
//
// ⚠️ «Spedito» manda la mail di spedizione al cliente vero, e su Amazon una
// conferma senza pacco ritirato e' una dichiarazione falsa: per questo passa
// SEMPRE dal dialog, e il percorso la propone solo dopo una prova di ritiro.
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { prettifyApiError } from "@/shared/api";
import { POLEEPO_BULK_STATUSES, bulkChangeStatus } from "./api";
import { STATO_SPEDITO, confermaSpedizioneBulk, riepilogoConfermaBulk } from "./confermaBulk";
import { nomeCanaleDiretto } from "./confermaCanaleApi";
import { SpedizioneBulkConfirmDialog } from "./components/SpedizioneBulkConfirmDialog";
import type { Order } from "./types";

interface Opts {
  onMessage: (msg: string) => void;
  onError: (msg: string) => void;
  onRefresh: () => void;
}

export interface StatoOrdine {
  /** Apre la conferma per lo stato scelto: «Spedito» col dialog spedizione. */
  chiedi: (stato: string) => void;
  busy: boolean;
  dialog: ReactNode;
}

const TITLE_ID = "cambia-stato-title";

function ConfermaStatoDialog({
  stato,
  busy,
  onConfirm,
  onCancel,
}: {
  stato: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    cancelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);
  const etichetta = POLEEPO_BULK_STATUSES.find((s) => s.value === stato)?.label ?? stato;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-line bg-card shadow-popover">
        <header className="border-b border-line px-5 py-4">
          <h2 id={TITLE_ID} className="font-display text-[18px] font-semibold text-ink">
            Cambiare lo stato in «{etichetta}»?
          </h2>
        </header>
        <p className="px-5 py-4 text-[13.5px] leading-relaxed text-ink">
          Lo stato cambia su Poleepo, e da lì sul canale dell&apos;ordine.
        </p>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <button
            ref={cancelRef}
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-md border border-line-2 bg-card px-3 py-1.5 text-[13px] font-semibold text-ink disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            data-testid="cambia-stato-ok"
            disabled={busy}
            onClick={onConfirm}
            className="rounded-md border border-emerald-700 bg-emerald-700 px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"
          >
            {busy ? "Cambio…" : "Sì, cambia"}
          </button>
        </footer>
      </div>
    </div>
  );
}

export function useStatoOrdine(
  order: Order | null,
  { onMessage, onError, onRefresh }: Opts,
): StatoOrdine {
  const [chiesto, setChiesto] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const chiudi = useCallback(() => {
    if (!busy) setChiesto(null);
  }, [busy]);

  const esegui = useCallback(async () => {
    if (!order || !chiesto) return;
    setBusy(true);
    try {
      if (chiesto === STATO_SPEDITO) {
        const { tono, testo } = riepilogoConfermaBulk(await confermaSpedizioneBulk([order.id]));
        if (tono === "ok") onMessage(testo);
        else onError(testo);
      } else {
        const r = await bulkChangeStatus([order.id], chiesto);
        const primo = r.errors[0];
        if (primo) onError(`Stato non cambiato: ${primo.error}`);
        else onMessage("Stato aggiornato.");
      }
    } catch (err) {
      onError(prettifyApiError(err));
    } finally {
      setBusy(false);
      setChiesto(null);
      onRefresh();
    }
  }, [order, chiesto, onMessage, onError, onRefresh]);

  let dialog: ReactNode = null;
  if (order && chiesto === STATO_SPEDITO) {
    const canale = nomeCanaleDiretto(order);
    dialog = (
      <SpedizioneBulkConfirmDialog
        quanti={1}
        canali={canale ? [canale] : []}
        quantiViaPoleepo={canale ? 0 : 1}
        busy={busy}
        onConfirm={() => void esegui()}
        onCancel={chiudi}
      />
    );
  } else if (order && chiesto) {
    dialog = (
      <ConfermaStatoDialog
        stato={chiesto}
        busy={busy}
        onConfirm={() => void esegui()}
        onCancel={chiudi}
      />
    );
  }
  return { chiedi: setChiesto, busy, dialog };
}
